/**
 * V4.48 — Hook + helpers pour le CA PRÉVISIONNEL (ancien "CA pondéré" probabiliste).
 *
 * Doctrine V4.48 — nomenclature canonique stricte :
 *   BRUT         = ca_brut_total      (avant rétrocession Pretto)
 *   PONDÉRÉ      = ca_broker          (après Pretto, avant commissions apporteur/parrain)
 *   NET          = ca_net             (après Pretto + commissions)
 *   PRÉVISIONNEL = ca_net × probabilité de transformation par étape (probabiliste)
 *
 * Le mot "pondéré" ne désigne PLUS le probabiliste — il désigne exclusivement
 * le CA courtier après retenue Pretto. Le probabiliste est le "PRÉVISIONNEL".
 *
 * Fonctionnement du PRÉVISIONNEL :
 *   - Coefficients issus de `/api/stats/conversion-rates` (12 derniers mois par défaut).
 *   - Si l'échantillon est trop faible (< 10 dossiers résolus), la projection est désactivée.
 *   - Transparence totale : les taux utilisés sont visibles via le modal d'audit.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "cf:caMode:v2"; // V4.48 — bump v2 (migration depuis anciennes valeurs)
const LEGACY_KEY = "cf:caMode";

/**
 * Migration one-shot des anciennes valeurs vers la nomenclature V4.48 :
 *   Legacy "brut"    -> "brut"     (avant Pretto)  — inchangé
 *   Legacy "net"     -> "pondere"  (après Pretto ; V4.34 appelait ce mode "net")
 *   Legacy "pondere" -> "brut"     (probabiliste — n'existe plus dans le toggle Dossiers ;
 *                                    remis à "brut" par défaut, l'utilisateur retrouvera le
 *                                    prévisionnel dans la page Cash)
 */
function migrateLegacy() {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const map = { brut: "brut", net: "pondere", pondere: "brut" };
    const migrated = map[legacy] || "brut";
    localStorage.setItem(STORAGE_KEY, migrated);
  } catch (e) { void e; }
}

export function useCaMode() {
  const [caMode, setCaMode] = useState(() => {
    try {
      migrateLegacy();
      const v = localStorage.getItem(STORAGE_KEY);
      return v === "brut" || v === "pondere" || v === "net" ? v : "brut";
    } catch {
      return "brut";
    }
  });
  const setMode = useCallback((m) => {
    setCaMode(m);
    try { localStorage.setItem(STORAGE_KEY, m); } catch (e) { void e; }
  }, []);
  return [caMode, setMode];
}

export function useConversionRates(windowMonths = 12) {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/stats/conversion-rates?window_months=${windowMonths}${force ? "&refresh=true" : ""}`);
      setRates(r.data);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [windowMonths]);

  useEffect(() => { load(); }, [load]);
  return { rates, loading, error, reload: () => load(true) };
}

/**
 * Calcule le CA PRÉVISIONNEL pour un dossier donné, à partir de son ca_net
 * et de la probabilité de transformation observée à son étape courante.
 * Retourne null si :
 *   - les taux ne sont pas chargés,
 *   - l'étape du dossier n'est pas une étape de conversion,
 *   - la fiabilité globale est faible (< 10 dossiers résolus).
 *
 * @param {object} dossier  - { ca_net, etape }
 * @param {object} rates    - réponse de /api/stats/conversion-rates
 * @returns {number|null}
 */
export function previsionnelCaForDossier(dossier, rates) {
  if (!dossier || !rates) return null;
  if (rates?.global?.fiabilite === "faible") return null;
  const stage = dossier.etape;
  const stageInfo = rates?.by_stage?.[stage];
  if (!stageInfo || stageInfo.p_signe == null) return null;
  const ca = Number(dossier.ca_net || 0);
  return Math.round(ca * stageInfo.p_signe);
}

/**
 * @deprecated V4.48 — utilise `previsionnelCaForDossier`.
 * Alias conservé pour rétro-compat courte durée.
 */
export const weightedCaForDossier = previsionnelCaForDossier;

/**
 * Détermine si la projection PRÉVISIONNELLE est globalement utilisable
 * (sample size global ≥ 10 dossiers résolus).
 */
export function isPrevisionnelAvailable(rates) {
  if (!rates) return false;
  return (rates?.global?.fiabilite || "faible") !== "faible";
}

/** @deprecated V4.48 — utilise `isPrevisionnelAvailable`. */
export const isPonderationAvailable = isPrevisionnelAvailable;

export const STAGE_LABEL_FR = {
  compromis: "Compromis",
  envoi_banque: "Envoi banque",
  accord_banque: "Accord banque",
  accord_risque: "Accord risque",
  offre_editee: "Offre éditée",
};
